/**
 * AppHeader - Flexible header component with per-page configuration
 * Mobile-optimized with proper touch handling and iOS safe area support
 */

import React, { ReactNode, useEffect, useRef } from 'react';
import './AppHeader.css';

export interface HeaderConfig {
  left?: ReactNode;
  center?: ReactNode;
  right?: ReactNode;
  backgroundColor?: string;
  variant?: 'default' | 'minimal' | 'transparent';
  className?: string;
}

interface AppHeaderProps {
  config: HeaderConfig;
}

const AppHeader: React.FC<AppHeaderProps> = ({ config }) => {
  const headerRef = useRef<HTMLElement>(null);

  const {
    left,
    center,
    right,
    backgroundColor = '#7c3aed',
    variant = 'default',
    className = ''
  } = config;

  // Prevent header from scrolling on iOS
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const preventScroll = (e: TouchEvent | WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const preventTouchMove = (e: TouchEvent) => {
      // Only prevent if it's a scroll gesture (not a tap)
      if (e.touches.length > 1) return; // Allow pinch zoom
      e.preventDefault();
      e.stopPropagation();
    };

    // Prevent scroll events on header
    header.addEventListener('touchmove', preventTouchMove, { passive: false });
    header.addEventListener('wheel', preventScroll, { passive: false });
    header.addEventListener('scroll', preventScroll, { passive: false });

    return () => {
      header.removeEventListener('touchmove', preventTouchMove);
      header.removeEventListener('wheel', preventScroll);
      header.removeEventListener('scroll', preventScroll);
    };
  }, []);

  return (
    <header
      ref={headerRef}
      className={`app-header app-header--${variant} ${className}`}
      style={{ backgroundColor }}
    >
      <div className="app-header__left">
        {left}
      </div>
      {center && (
        <div className="app-header__center">
          {center}
        </div>
      )}
      <div className="app-header__right">
        {right}
      </div>
    </header>
  );
};

export default AppHeader;
